import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, take } from 'rxjs';

import { Service } from 'src/app/models/service.model';
import { Permission } from 'src/app/shared/constants/permissions';
import * as fromStore from 'src/app/store/';
import { CreateTicketComponent } from 'src/app/tickets/containers';
import { LocationNameComponent } from '../location-name/location-name.component';

@Component({
  selector: 'app-inventory-detail',
  templateUrl: './inventory-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class InventoryDetailComponent implements OnInit {
  @Input() services: Service[];

  @Input() selectedService: Service;

  @Input() activeServices: Service[];

  @Input() isLoading: boolean;

  @Input() isServicesInProgress: boolean;

  private store = inject(Store<fromStore.State>);

  private destroyRef = inject(DestroyRef);

  readonly isServiceIdOnRouter =
    !!inject(ActivatedRoute).snapshot.paramMap.get('serviceId');

  services$: Observable<Service[]>;

  service$: Observable<Service>;

  Permission = Permission;

  private dialog = inject(MatDialog);

  private cdr = inject(ChangeDetectorRef);

  ngOnInit(): void {
    if (this.isServiceIdOnRouter) {
      this.initializeFromRouter();
    }
  }

  initializeFromRouter(): void {
    this.store
      .select(fromStore.getServicesLoaded)
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((loaded) => {
        if (!loaded) {
          this.store.dispatch(new fromStore.LoadServices());
        }
      });

    this.service$ = this.store.select(fromStore.getCurrentRouterService);
    this.service$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((service) => {
        if (service?.Id) {
          this.selectedService = service;
          this.store.dispatch(new fromStore.SetSelectedService(service.Id));
          this.cdr.markForCheck();
        }
      });
    this.services$ = this.store.select(fromStore.getAllServices);
    this.services$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((services) => {
        this.services = services;
        this.cdr.markForCheck();
      });
  }

  onCreateTicket(): void {
    const dialogConfig: MatDialogConfig = {
      autoFocus: true,
      minHeight: '200px',
      maxHeight: 'calc(100vh - 20px)',
      width: '560px',
      maxWidth: 'calc(100vw - 20px)',
      data: {
        originPage: 'Service',
        selectedServiceId: this.selectedService.Id,
      },
      disableClose: true,
    };

    this.dialog.open(CreateTicketComponent, dialogConfig);
  }

  onEditLocationName(service: Service): void {
    const dialogConfig: MatDialogConfig = {
      autoFocus: true,
      minHeight: '200px',
      maxHeight: 'calc(100vh - 20px)',
      width: '560px',
      maxWidth: 'calc(100vw - 20px)',
      data: {
        originPage: 'Service',
        locationId: service.Z_Location__c,
        locationAddress: service.Z_Location__r.Name,
        banLookupId: service.BanLookupId,
        customerLocationAlias: service.CustomerLocationAlias,
        services: this.services,
      },
      disableClose: false,
      ariaLabelledBy: 'location-name-dialog-title',
    };

    this.dialog.open(LocationNameComponent, dialogConfig);
  }
}
